import { useEffect, useState } from "react";
import { House, Archive } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { WelcomeHeader } from "../components/client/WelcomeHeader";
import { StoreRevenueCard } from "../components/client/StoreRevenueCard";
import { OrderVolumeCard } from "../components/client/OrderVolumeCard";
import { ROIGauge } from "../components/client/ROIGauge";
import { ProjectionsCard } from "../components/client/ProjectionsCard";
import { PerformanceReports } from "../components/client/PerformanceReports";
import { StoreUpdatesFeed } from "../components/client/StoreUpdatesFeed";
import { ReferralTracker } from "../components/client/ReferralTracker";
import { LockedUpsells } from "../components/client/LockedUpsells";
import { YourIGCDashboard } from "../components/client/YourIGCDashboard";
import { InitializingState } from "../components/client/InitializingState";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { SUPPORT_EMAIL } from "../constants/brand";
import { getClientNavItems } from "../modules/clientNav";
import { formatET } from "../lib/dateUtils";

export default function ClientDashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [igcDashboard, setIgcDashboard] = useState(null);
  const navItems = getClientNavItems(user?.unlocked_modules);

  const fetchData = async () => {
    const { data: d } = await api.get("/client/dashboard");
    setData(d);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    api.get("/client/igc-dashboard").then(({ data: d }) => setIgcDashboard(d)).catch(() => setIgcDashboard(null));
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen bg-igc-bg flex items-center justify-center">
        <Archive size={32} className="text-igc-goldText animate-pulse" />
      </div>
    );
  }

  const lastUpdated = data.last_updated_at
    ? formatET(data.last_updated_at, "MMM d, yyyy")
    : data.updates[0]
    ? formatET(data.updates[0].timestamp, "MMM d, yyyy")
    : "—";

  return (
    <DashboardLayout navItems={navItems}>
      {data.status === "initializing" ? (
        <InitializingState displayName={user.display_name} />
      ) : (
        <div data-testid="client-dashboard">
          <WelcomeHeader displayName={user.client_id || user.display_name} status="Active" lastUpdated={lastUpdated} />

          {igcDashboard && <YourIGCDashboard data={igcDashboard} />}

          {user.client_id === "SAB001" && (
            <a href="/client/funding-pipeline" className="block mb-6 bg-igc-surface border border-igc-gold/30 p-6 hover:border-igc-gold/60 transition-colors" data-testid="sab001-funding-pipeline-preview" title="Opens your live Funding Pipeline — track every deal and your 20% commission in real time.">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <p className="font-serif text-lg text-igc-cream">Funding Pipeline</p>
                <span className="text-[10px] uppercase tracking-widest text-igc-goldText bg-igc-gold/10 px-2 py-1">View Live Deals →</span>
              </div>
              <p className="text-igc-muted text-sm">
                Track every funding deal and your 20% commission in real time, plus the full status timeline for each deal.
              </p>
            </a>
          )}

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-6">
            <div className="md:col-span-4"><StoreRevenueCard totalRevenue={data.total_revenue} monthlyBreakdown={data.monthly_breakdown} currentMonthRevenue={data.current_month_revenue} currentMonthLabel={data.current_month_label} /></div>
            <div className="md:col-span-4"><OrderVolumeCard ordersPlaced={data.orders_placed} ordersFulfilled={data.orders_fulfilled} fulfillmentRate={data.fulfillment_rate} orderBreakdown={data.order_breakdown} /></div>
            <div className="md:col-span-4"><ROIGauge roiPct={data.roi_recovered_pct} initialInvestment={data.initial_investment} revenueEarned={data.total_revenue} amountRemaining={data.amount_remaining} monthsCurrent={data.estimated_months_current_rate} monthsRamp={data.estimated_months_full_ramp} fullyRecovered={data.fully_recovered} /></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-10">
            <div className="md:col-span-8"><ProjectionsCard projections={data.projections} monthsOfData={data.monthly_breakdown.length} /></div>
            <div className="md:col-span-4"><ReferralTracker referralsMade={data.referrals_made} pendingCommissions={data.pending_commissions} earnedCommissions={data.earned_commissions} onRefresh={fetchData} /></div>
          </div>

          <div className="mb-6"><PerformanceReports reports={data.reports} /></div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-6">
            <div className="md:col-span-6"><StoreUpdatesFeed updates={data.updates} /></div>
            <div className="md:col-span-6"><LockedUpsells /></div>
          </div>

          <p className="text-igc-muted text-xs text-center mt-10">Questions about your store? Contact us at {SUPPORT_EMAIL}</p>
        </div>
      )}
    </DashboardLayout>
  );
}
